import _, { escapeRegExp } from "lodash";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useTransition,
} from "react";
import { useTranslation } from "react-i18next";
import { InputActionMeta } from "react-select";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue } from "recoil";

import {
  useDropdownSourceContext,
  useDynamicFormsUtils,
  usePageContext,
  useTableFiltersActions,
} from "@ryde/redux";
import {
  ApiSuffixType,
  CrudApi,
  dropdownAtomFamily,
  filterActiveDropdownItems,
  IDropdownItem,
  IDropdownItemsParams,
  ISearchCriteria,
  ISimpleDto,
  ISimplePassengerDto,
  Log,
  NO_VALUE_DISPLAY,
  normalizeToArrayValue,
  RydeUiUnit,
  sortFilterDropdownArray,
  sortFiltersFnFactory,
  tableFiltersSchemaAtomFamily,
  TDropdownNullableOption,
  transformDropdownDtos,
  useSystemParams,
  ValueType,
} from "@ryde/shared";

import { IFilterMultiProps } from "../parts/filter-multiselect-models";

export const useFilterMultiselectBound = (props: IFilterMultiProps) => {
  const { t } = useTranslation();
  const { entity: currentPageEntity } = usePageContext();
  const filterSchema = useRecoilValue(
    tableFiltersSchemaAtomFamily(currentPageEntity),
  );
  const { customFieldId, initialCheckedValues } = props;
  const [isOpen, setIsOpen] = useState(false);
  const { setValuesToFilter } = useTableFiltersActions();
  const { getDropdownItems } = useDropdownSourceContext();
  const { hideInactiveInFilterDropdowns } = useSystemParams();
  const {
    getControlDropdownSource,
    getControlDropdownSourceCacheKey,
    getControlFormAttributes,
    getGridControlLabelById,
    getControlFilterDropdownCriteria,
    getCustomFieldById,
  } = useDynamicFormsUtils();

  // TODO ADD DISABLE OPTION BY FORM ATTR
  const isSelectNoneOptionEnabled = true;
  const [input, setInput] = useState("");
  const [dropdownValue, setDropdownValue] = useState<IDropdownItem[]>([]);
  const [, startTransition] = useTransition();
  const valueValuesSet = useMemo(() => {
    return new Set(dropdownValue.map(({ value }) => value));
  }, [dropdownValue]);

  const dropdownSourceEntityName = useMemo(
    () => (customFieldId ? getControlDropdownSource(customFieldId) : undefined),
    [customFieldId, getControlDropdownSource],
  );

  const dropdownSourceEntityIdentifier = useMemo(
    () => getControlDropdownSourceCacheKey(customFieldId),
    [customFieldId, getControlDropdownSourceCacheKey],
  );

  const formAttributes = useMemo(
    () => (customFieldId ? getControlFormAttributes(customFieldId) : undefined),
    [customFieldId, getControlFormAttributes],
  );

  const { dropdownSortingDirection, disableSorting } = useMemo(() => {
    const sortingAttributes = {
      dropdownSortingDirection: "asc",
      disableSorting: false,
    };

    if (customFieldId) {
      const { dropItemsSortingDirections, disableSorting: disableSortingAttr } =
        getCustomFieldById(customFieldId)?.formAttributes || {};

      if (disableSortingAttr) {
        sortingAttributes.disableSorting = disableSorting;
      }

      if (dropItemsSortingDirections) {
        sortingAttributes.dropdownSortingDirection = dropItemsSortingDirections;
      }
    }

    return sortingAttributes;
  }, [customFieldId, getCustomFieldById]);

  const dropdownFilterDropdownCriteria = useMemo(
    () =>
      customFieldId
        ? getControlFilterDropdownCriteria(customFieldId)
        : undefined,
    [customFieldId, getControlFilterDropdownCriteria],
  );

  const [dropdownItems, setDropdownItems] = useRecoilState(
    dropdownAtomFamily(dropdownSourceEntityIdentifier),
  );

  const multiselectLabel = useMemo((): string => {
    if (customFieldId) {
      return getGridControlLabelById(customFieldId);
    }

    return "";
  }, [customFieldId, getGridControlLabelById]);

  const isSelectNoneValueActive = useMemo(() => {
    let isActive = false;

    if (customFieldId && isSelectNoneOptionEnabled) {
      isActive =
        Array.isArray(filterSchema?.[customFieldId]) &&
        filterSchema[customFieldId].some(id => !id);
    }

    return isActive;
  }, [customFieldId, filterSchema, isSelectNoneOptionEnabled]);

  const onItemsCheckChange = useCallback(
    (newValue: IDropdownItem[]): void => {
      if (newValue && customFieldId) {
        const newValues: TDropdownNullableOption[] = (newValue?.map(
          item => item.value,
        ) || []) as number[] | string[];

        if (isSelectNoneValueActive) {
          newValues.unshift(null);
        }

        Log.debug("Update filter value for field: " + customFieldId, newValue);
        setDropdownValue(newValue as IDropdownItem[]);
        setValuesToFilter?.(
          customFieldId,
          newValues as number[] | string[] | null[],
        );
      }
    },
    [customFieldId, isSelectNoneValueActive, setValuesToFilter],
  );

  const activeFieldName =
    formAttributes?.dropdownItemsFilter?.customActiveFieldName;

  const activeDropdownItems = useMemo(() => {
    if (!hideInactiveInFilterDropdowns) {
      return dropdownItems;
    }

    return (
      filterActiveDropdownItems({
        items: dropdownItems,
        activeFieldName,
        isSelected: item => valueValuesSet.has(item.value),
      }) || []
    );
  }, [
    activeFieldName,
    dropdownItems,
    hideInactiveInFilterDropdowns,
    valueValuesSet,
  ]);

  const inputToFilter = useMemo(() => input.toLowerCase().trim(), [input]);
  const filteredDropDownItems = useMemo(
    () =>
      inputToFilter
        ? activeDropdownItems.filter(({ label }) =>
            String(label).toLowerCase().match(escapeRegExp(inputToFilter)),
          )
        : activeDropdownItems,
    [activeDropdownItems, inputToFilter],
  );

  const toggleFilterSelectNoneValue = useEvent(() => {
    if (!customFieldId) {
      return;
    }

    const newValue = !isSelectNoneValueActive;

    const dropdownItemsValues: TDropdownNullableOption[] = dropdownValue.map(
      ({ value }) => value,
    );

    if (newValue) {
      dropdownItemsValues.unshift(null);
    }

    setValuesToFilter(
      customFieldId,
      dropdownItemsValues as number[] | string[] | null[],
    );

    return newValue;
  });

  const isSelectedAll = useMemo(() => {
    const isAllFilteredDropDownItemsSelected = filteredDropDownItems?.every(
      ({ value }) => valueValuesSet.has(value),
    );

    // maybe we would return this behavior
    // if (isSelectNoneOptionEnabled) {
    //   return isAllFilteredDropDownItemsSelected && isSelectNoneValueActive;
    // }

    return isAllFilteredDropDownItemsSelected;
  }, [filteredDropDownItems, valueValuesSet]);

  const selectAll = useEvent((): void => {
    if (!customFieldId) {
      return;
    }

    const filteredDropDownItemsValues = filteredDropDownItems.map(
      ({ value }) => value,
    );

    const includedFilteredDropdownItems = [
      ...dropdownValue,
      ...filteredDropDownItems,
    ];
    const filteredDropDownItemsValuesSet = new Set(filteredDropDownItemsValues);
    const excludedFilteredDropdownItems = dropdownValue.filter(
      ({ value }) => !filteredDropDownItemsValuesSet.has(value),
    );
    const includedFilteredDropdownItemsValues: TDropdownNullableOption[] =
      includedFilteredDropdownItems.map(({ value }) => value);
    const excludedFilteredDropdownItemsValues: TDropdownNullableOption[] =
      excludedFilteredDropdownItems.map(({ value }) => value);

    setDropdownValue(
      !isSelectedAll
        ? includedFilteredDropdownItems
        : excludedFilteredDropdownItems,
    );

    const valuesToFilterDropdownItemsValues = !isSelectedAll
      ? includedFilteredDropdownItemsValues
      : excludedFilteredDropdownItemsValues;

    // maybe we would return this behavior
    // if (isSelectNoneOptionEnabled) {
    //   const newSelectNoneValueOption = !isSelectedAll;
    //
    //   // if (newSelectNoneValueOption) {
    //   //   valuesToFilterDropdownItemsValues.unshift(null);
    //   // } else {
    //   //   valuesToFilterDropdownItemsValues.filter(val => !!val);
    //   // }
    // }

    setValuesToFilter(
      customFieldId,
      valuesToFilterDropdownItemsValues as number[] | string[] | null[],
    );
  });

  const loadDropdownItemsFiltered = useEvent(
    async (entity: RydeUiUnit, criteria: ISearchCriteria<unknown>) => {
      const simpleDtos = await CrudApi.post<
        ISimpleDto[] | ISimplePassengerDto[]
      >({
        entity,
        suffixType: ApiSuffixType.DropdownItemsFiltered,
        data: criteria,
      });

      if (!simpleDtos?.length) {
        return [];
      }

      const items = transformDropdownDtos({ dtos: simpleDtos, entity });
      return items;
    },
  );

  const loadFilterDropdownItems = useCallback(async () => {
    if (dropdownSourceEntityName) {
      let items: IDropdownItem[] = [];

      if (dropdownFilterDropdownCriteria) {
        items = await loadDropdownItemsFiltered?.(
          dropdownSourceEntityName,
          dropdownFilterDropdownCriteria,
        );
      } else {
        const dropDownItemsParams: IDropdownItemsParams = {
          dropdownItemsSuffixOverride:
            formAttributes?.dropdownItemsSuffixOverride,
          dropdownItemsResolverName: formAttributes?.dropdownItemsResolverName,
        };
        items = await getDropdownItems?.(
          dropdownSourceEntityName,
          false,
          dropDownItemsParams,
        );
      }

      if (!items || !_.isArray(items) || !items.length) {
        setDropdownItems([]);
      } else if (disableSorting) {
        setDropdownItems(items);
      } else {
        const sortedItems = items.toSorted(
          sortFiltersFnFactory(true, dropdownSortingDirection === "desc"),
        );
        setDropdownItems(sortedItems);
      }
    } else {
      Log.warn(t("errors.invalidDropdownSourceInFilter"));
      setDropdownItems([]);
    }
  }, [
    dropdownSourceEntityName,
    dropdownFilterDropdownCriteria,
    disableSorting,
    loadDropdownItemsFiltered,
    formAttributes?.dropdownItemsSuffixOverride,
    formAttributes?.dropdownItemsResolverName,
    getDropdownItems,
    setDropdownItems,
    dropdownSortingDirection,
    t,
  ]);

  useEffect(() => {
    if (!isOpen) {
      setDropdownItems(items =>
        sortFilterDropdownArray(
          items,
          dropdownValue,
          true,
          dropdownSortingDirection === "desc",
        ),
      );
    }

    /* sort items - put selected items on top while reopening dropdown (but not on selecting each item)*/
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, dropdownSortingDirection]);

  const setInitialDropdownValues = useCallback(
    (items: IDropdownItem[], filterIds?: ValueType | null) => {
      if (filterIds && customFieldId) {
        const initialDropdownValueItems = items.filter(v =>
          normalizeToArrayValue(filterIds).some(
            x => String(x) === String(v.value),
          ),
        );
        Log.debug(
          "Set initial value for filter field: " + customFieldId,
          initialDropdownValueItems,
          filterIds,
        );
        setDropdownValue(initialDropdownValueItems);
      } else if (filterIds === null) {
        setDropdownValue([]);
      }
    },
    [customFieldId],
  );
  useEffect(() => {
    if (customFieldId) {
      startTransition(() => {
        loadFilterDropdownItems();
      });
    }
  }, [customFieldId, loadFilterDropdownItems]);

  useEffect(() => {
    startTransition(() => {
      setInitialDropdownValues(dropdownItems, initialCheckedValues);
    });
  }, [dropdownItems, initialCheckedValues, setInitialDropdownValues]);

  const buttonContent = useMemo(() => {
    const defaultLabel =
      isSelectNoneValueActive && !dropdownValue.length
        ? t("general.SELECT_NONE")
        : NO_VALUE_DISPLAY;

    const incrementValue = isSelectNoneValueActive ? 1 : 0;
    const selectedValuesNumber = dropdownValue.length + incrementValue;

    return {
      labelBlack: `${
        dropdownValue.length
          ? dropdownItems.find(item => item.value === dropdownValue[0].value)
              ?.label
          : defaultLabel
      }`,
      labelBlue: `${selectedValuesNumber > 1 ? `+${selectedValuesNumber - 1}` : ""}`,
    };
  }, [isSelectNoneValueActive, dropdownItems, t, dropdownValue]);

  const onInputChange = useEvent(
    (inputValue: string, action: InputActionMeta): void => {
      if (action.action === "input-change") {
        setInput(inputValue);
      }
    },
  );

  return {
    filteredDropDownItems,
    isOpen,
    setIsOpen,
    value: dropdownValue,
    buttonContent,
    multiselectLabel,
    onItemsCheckChange,
    dropdownItems,
    selectAll,
    isSelectedAll,
    input,
    onInputChange,
    toggleFilterSelectNoneValue,
    isSelectNoneValueActive,
    isSelectNoneOptionEnabled,
  };
};
